/**
 * Toast container — rendered once at the app shell.
 *
 * Sits in the bottom-right corner on desktop and bottom-center on
 * mobile. Each toast is a small card with kind-coloured borders,
 * an optional action button, and a dismiss "x".
 *
 * Live-region semantics:
 *   - `info` / `success` use `role="status"` + `aria-live="polite"`
 *     so a screen reader announces them after the current utterance.
 *   - `warning` / `error` use `role="alert"` + `aria-live="assertive"`
 *     so they interrupt — these are usually consequences of the user's
 *     own action (a save failure), so interruption is justified.
 */

import { CheckCircle2, Info, TriangleAlert, X, XCircle } from "lucide-react";
import { useDismissToast, useToasts, type Toast, type ToastKind } from "../lib/toast";

const KIND_STYLES: Record<ToastKind, { card: string; icon: typeof Info; iconClass: string }> = {
  info: {
    card: "border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/80 dark:text-sky-100",
    icon: Info,
    iconClass: "text-sky-600 dark:text-sky-300",
  },
  success: {
    card: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/80 dark:text-emerald-100",
    icon: CheckCircle2,
    iconClass: "text-emerald-600 dark:text-emerald-300",
  },
  warning: {
    card: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/80 dark:text-amber-100",
    icon: TriangleAlert,
    iconClass: "text-amber-600 dark:text-amber-300",
  },
  error: {
    card: "border-red-300 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/80 dark:text-red-100",
    icon: XCircle,
    iconClass: "text-red-600 dark:text-red-300",
  },
};

export function ToastContainer() {
  const toasts = useToasts();
  const dismiss = useDismissToast();

  if (toasts.length === 0) return null;

  return (
    <div
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[2000] flex flex-col items-center gap-2 px-4 sm:bottom-6 sm:right-6 sm:left-auto sm:items-end sm:px-0"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const styles = KIND_STYLES[toast.kind];
  const Icon = styles.icon;
  const isAlert = toast.kind === "error" || toast.kind === "warning";

  return (
    <div
      role={isAlert ? "alert" : "status"}
      aria-live={isAlert ? "assertive" : "polite"}
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border px-3 py-2.5 shadow-lg ${styles.card}`}
    >
      <Icon className={`mt-0.5 size-4 shrink-0 ${styles.iconClass}`} aria-hidden />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium leading-snug">{toast.message}</p>
        {toast.description && <p className="text-xs opacity-80">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
            className="mt-1 inline-flex items-center text-xs font-medium underline underline-offset-2 hover:no-underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="ml-1 shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}
